import React, { useEffect, useState } from 'react';
import { Field, ErrorMessage, HelperMessage, ValidMessage } from '@atlaskit/form';
import { LoadingButton } from '@atlaskit/button';
import TextArea from '@atlaskit/textarea';
import {
  AlertableSignalFlowInput,
  IndicatorDTOHack,
  SloInstanceDto,
} from '@atlassiansox/phobos-client-ts';
import { css } from '@emotion/core';

import {
  useFetchErrorController,
  SignalFlowValidateResultType,
  SignalFlowValidateContext,
} from '@/controllers';
import { FormFieldTooltipLabel } from '@/components/primitive';
import { ConditionalErrorMessage } from '@/components/composed/forms/form-errors';
import { isIndicatorTypeMimir } from '@/components/composed/slo-group/utils';

import { helperCopy } from '../../copy';

import QueryUnfilledWarning from './query-unfilled-warning';
import QuerySumByWarning from './query-sum-by-warning';
import { fetchSignalFlowAlertable } from './utils';
import { testIds } from './constants';

type SignalFlowValidDisplayProps = {
  validateResult: SignalFlowValidateResultType;
  displayType?: SignalFlowValidDisplayType;
  query?: string;
};

enum SignalFlowValidDisplayType {
  General = 'general',
  GoodQuery = 'goodQuery',
  BadQuery = 'badQuery',
}

const errorContainerStyles = css`
  max-width: 490px;
  word-break: break-word;
`;

const SignalFlowValidDisplay: React.FC<SignalFlowValidDisplayProps> = ({
  validateResult,
  displayType,
  query = '',
}) => {
  const queryFilled: boolean = query.includes('fill(0)');
  const queryHasOnlySumBy: boolean = query.includes('.sum(by=') && !query.includes('.sum()');

  switch (displayType) {
    case SignalFlowValidDisplayType.GoodQuery: {
      if (validateResult && !validateResult.goodQueryErrors?.length) {
        return <ValidMessage>Your good query is valid!</ValidMessage>;
      }

      const queryHasNoDataPoints: boolean =
        validateResult?.goodQueryErrors.some(x =>
          x.errorMessage.includes('No data points found')
        ) ?? false;

      const shouldShowFillWarning: boolean = queryHasNoDataPoints && !queryFilled;
      const shouldShowSumByWarning: boolean =
        queryHasNoDataPoints && queryFilled && queryHasOnlySumBy;

      return (
        <>
          {validateResult && (
            <ErrorMessage>
              <div css={errorContainerStyles}>
                {validateResult.goodQueryErrors.map((x, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={`${x.errorMessage}-${index}`}>{x.errorMessage}</div>
                ))}
              </div>
            </ErrorMessage>
          )}
          {shouldShowFillWarning && <QueryUnfilledWarning />}
          {shouldShowSumByWarning && <QuerySumByWarning />}
        </>
      );
    }

    case SignalFlowValidDisplayType.BadQuery: {
      if (validateResult && !validateResult.badQueryErrors?.length) {
        return <ValidMessage>Your bad query is valid!</ValidMessage>;
      }

      const queryHasNoDataPoints: boolean =
        validateResult?.badQueryErrors.some(x => x.errorMessage.includes('No data points found')) ??
        false;

      const shouldShowFillWarning: boolean = queryHasNoDataPoints && !queryFilled;
      const shouldShowSumByWarning: boolean =
        queryHasNoDataPoints && queryFilled && queryHasOnlySumBy;

      return (
        <>
          {validateResult && (
            <ErrorMessage>
              <div css={errorContainerStyles}>
                {validateResult?.badQueryErrors.map((x, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={`${x.errorMessage}-${index}`}>{x.errorMessage}</div>
                ))}
              </div>
            </ErrorMessage>
          )}
          {shouldShowFillWarning && <QueryUnfilledWarning />}
          {shouldShowSumByWarning && <QuerySumByWarning />}
        </>
      );
    }

    case SignalFlowValidDisplayType.General: {
      if (!validateResult?.generalErrors?.length) {
        return <></>;
      }

      const errors: string[] = [
        ...validateResult?.generalErrors,
        'This is probably because the SignalFlow is syntactically invalid.',
      ];

      return (
        <ErrorMessage>
          <div css={errorContainerStyles}>
            {errors.map((x, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`${x}-${index}`}>{x}</div>
            ))}
          </div>
        </ErrorMessage>
      );
    }
  }

  return <></>;
};

export interface CheckPayload {
  goodQuery: string;
  badQuery: string;
  // NOTE: Cool so this is received as a number. But when it's modified in the Form Field it's actually a string. /shrug
  objective: number | string;
}

type SignalFlowQueryFieldsProps = Pick<CheckPayload, 'goodQuery' | 'badQuery'> & {
  isDisabled: boolean;
  indicator?: IndicatorDTOHack;
  sloInstance?: SloInstanceDto;
};

const SignalFlowQueryFields: React.FC<SignalFlowQueryFieldsProps> = ({
  goodQuery,
  badQuery,
  isDisabled,
  indicator,
  sloInstance,
}) => {
  const { onError } = useFetchErrorController();

  const [hasValidated, setHasValidated] = useState<boolean>(false);
  const [validateResult, setValidateResult] = useState<SignalFlowValidateResultType>();
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [signalFlowQueryState, setSignalFlowQueryState] = useState<CheckPayload>({
    goodQuery: '',
    badQuery: '',
    objective: 0,
  });

  // Side effect from upstream form validate
  const { error: formValidateError, isValidating: isFormValidating } = React.useContext(
    SignalFlowValidateContext
  );
  useEffect(() => {
    setValidateResult(formValidateError);
    setHasValidated(true);
  }, [formValidateError]);

  // Local handlers
  const updateSignalFlowQueryState = (_: string, state: CheckPayload) => {
    setSignalFlowQueryState(state);
  };

  const handleValidate = async () => {
    if (!hasValidated) setHasValidated(true);
    setIsValidating(true);

    try {
      const payload: AlertableSignalFlowInput = {
        goodQuery: signalFlowQueryState.goodQuery,
        badQuery: signalFlowQueryState.badQuery,
        objective: Number(signalFlowQueryState.objective),
      };
      const result: SignalFlowValidateResultType = await fetchSignalFlowAlertable(payload);
      setValidateResult(result);
    } catch (err) {
      onError(err);
    }

    setIsValidating(false);
  };

  const isMimir = isIndicatorTypeMimir(indicator) && sloInstance?.query;
  const labelPrefix = isMimir ? 'Mimir' : 'SignalFlow';

  // Computed variables by state and props
  const isFormDisabled: boolean = isDisabled || isValidating || isFormValidating;
  const isGeneralError: boolean = Boolean(validateResult?.generalErrors?.length);

  const shouldShowValidateButton: boolean =
    !isMimir && !!signalFlowQueryState.goodQuery && !!signalFlowQueryState.badQuery;

  const isGoodQueryInvalid: boolean =
    Boolean(validateResult?.goodQueryErrors?.length) && !isFormDisabled;
  const isBadQueryInvalid: boolean =
    Boolean(validateResult?.badQueryErrors?.length) && !isFormDisabled;

  const shouldShowGoodBadMessages: boolean = hasValidated && !isFormDisabled && !isGeneralError;
  const shouldShowGeneralMessage: boolean = hasValidated && !isFormDisabled && isGeneralError;

  return (
    <>
      <Field<string, HTMLTextAreaElement>
        name="goodQuery"
        label={
          <FormFieldTooltipLabel
            label={`${labelPrefix} - Good Query`}
            content={helperCopy.slo[isMimir ? 'mimirGoodQueries' : 'signalFlowGoodQueries']}
          />
        }
        defaultValue={isMimir ? sloInstance?.query?.good_query : goodQuery}
        validate={updateSignalFlowQueryState}
        isDisabled={isFormDisabled}
      >
        {({ fieldProps: { isInvalid, ...rest }, error }) => (
          <>
            {/* @ts-ignore */}
            <TextArea
              isMonospaced
              minimumRows={6}
              spellCheck={false}
              {...rest}
              isInvalid={isGoodQueryInvalid || isInvalid}
            />
            <ConditionalErrorMessage error={error} />
          </>
        )}
      </Field>
      {shouldShowGoodBadMessages && (
        <SignalFlowValidDisplay
          validateResult={validateResult}
          query={signalFlowQueryState.goodQuery}
          displayType={SignalFlowValidDisplayType.GoodQuery}
        />
      )}
      <Field<string, HTMLTextAreaElement>
        name="badQuery"
        label={
          <FormFieldTooltipLabel
            label={`${labelPrefix} - Bad Query`}
            content={helperCopy.slo[isMimir ? 'mimirBadQueries' : 'signalFlowBadQueries']}
          />
        }
        defaultValue={isMimir ? sloInstance?.query?.bad_query : badQuery}
        validate={updateSignalFlowQueryState}
        isDisabled={isFormDisabled}
      >
        {({ fieldProps: { isInvalid, ...rest }, error }) => (
          <>
            {/* @ts-ignore */}
            <TextArea
              isMonospaced
              minimumRows={6}
              spellCheck={false}
              {...rest}
              isInvalid={isBadQueryInvalid || isInvalid}
            />
            <ConditionalErrorMessage error={error} />
          </>
        )}
      </Field>
      {shouldShowGoodBadMessages && (
        <SignalFlowValidDisplay
          validateResult={validateResult}
          query={signalFlowQueryState.badQuery}
          displayType={SignalFlowValidDisplayType.BadQuery}
        />
      )}

      {shouldShowGeneralMessage && (
        <SignalFlowValidDisplay
          validateResult={validateResult}
          displayType={SignalFlowValidDisplayType.General}
        />
      )}

      {(isValidating || isFormValidating) && (
        <HelperMessage>Checking that your queries are valid...</HelperMessage>
      )}

      {shouldShowValidateButton && (
        <LoadingButton
          appearance="primary"
          spacing="compact"
          testId={testIds.validateSFxButton}
          onClick={handleValidate}
          isLoading={isValidating}
          isDisabled={isFormDisabled}
        >
          Validate SignalFlow Good &amp; Bad Queries
        </LoadingButton>
      )}
    </>
  );
};

export default SignalFlowQueryFields;
